import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mqtt from 'mqtt';

// ─────────────────────────────────────────────────────────────────────────────
// MQTT connection settings — supplied at BUILD time from portal/.env
// (see portal/.env.example). .env is gitignored, so these stay out of the repo.
//
// SECURITY WARNING — read before go-live:
// Create React App inlines every REACT_APP_* value into the production JS
// bundle. Moving these out of source keeps them off GitHub, but it does NOT
// hide them from users: anyone who opens devtools on app.fluidstream.co can
// read this broker password and then publish to units/+/from/# themselves.
// Before this is customer-facing, replace with either
//   a) per-user broker credentials minted by the API after login, or
//   b) a token-authenticated WebSocket proxy fronted by the API,
// and restrict the shared m241-unit001 account to the to/# topics only.
// ─────────────────────────────────────────────────────────────────────────────
const MQTT_URL  = process.env.REACT_APP_MQTT_URL  || 'wss://mqtt.fluidstream.co:9001';
const MQTT_USER = process.env.REACT_APP_MQTT_USER || '';
const MQTT_PASS = process.env.REACT_APP_MQTT_PASS || '';

/* ── Design tokens — dark industrial ── */
const T = {
  bg:          '#0d1117',
  bg2:         '#161b22',
  bg3:         '#1c2333',
  border:      '#30363d',
  fg:          '#e6edf3',
  fg2:         '#8b949e',
  accent:      '#1f8ef1',
  accentDim:   '#0d4b8a',
  good:        '#3fb950',
  warn:        '#d29922',
  crit:        '#f85149',
  card:        '#161b22',
  mono:        "'IBM Plex Mono', 'Roboto Mono', monospace",
  ui:          "'IBM Plex Sans', system-ui, sans-serif",
};

const PRESS_UNITS = ['psi', 'KPa', 'oz'];
const TEMP_UNITS  = ['°C', '°F'];

/* ── Nav structure ── */
const NAV = [
  { id: 'live',    label: 'Main',          icon: <GridIcon /> },
  { id: 'control', label: 'Control Setup', icon: <SettingsIcon /> },
  { id: 'alarm',   label: 'Alarms',        icon: <BellIcon />,   badge: true },
  { id: 'trend',   label: 'Trend',         icon: <TrendIcon /> },
  { id: 'rundata', label: 'Run Data',      icon: <MonitorIcon /> },
  { id: 'admin',   label: 'Admin Setup',   icon: <UserIcon /> },
];

/* ══ SVG ICONS ══ */
function GridIcon()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>; }
function SettingsIcon(){ return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>; }
function BellIcon()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>; }
function TrendIcon()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>; }
function MonitorIcon() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg>; }
function UserIcon()    { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>; }

/* ══ KPI TILE ══ */
function KpiTile({ label, value, unit, accent }) {
  const borderColor = accent === 'good' ? T.good : accent === 'warn' ? T.warn : accent === 'crit' ? T.crit : T.border;
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderTop: `2px solid ${borderColor}`,
      borderRadius: 8, padding: '12px 14px', minWidth: 0,
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: T.fg2, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 500,
        lineHeight: 1, color: T.fg, fontVariantNumeric: 'tabular-nums' }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 11, color: T.fg2, marginTop: 4 }}>{unit}</div>
    </div>
  );
}

/* ══ SENSOR ITEM ══ */
function SensorItem({ label, value, unit, pct, accentColor }) {
  const fillColor = accentColor || T.accent;
  return (
    <div style={{
      background: T.bg3, border: `1px solid ${T.border}`,
      borderRadius: 6, padding: '10px 12px',
    }}>
      <div style={{ fontSize: 11, color: T.fg2, marginBottom: 4, fontWeight: 500 }}>{label}</div>
      <div>
        <span style={{ fontFamily: T.mono, fontSize: 18, fontWeight: 500,
          fontVariantNumeric: 'tabular-nums', color: T.fg }}>{value ?? '—'}</span>
        {unit && <span style={{ fontSize: 11, color: T.fg2, marginLeft: 3 }}>{unit}</span>}
      </div>
      {pct !== undefined && (
        <div style={{ height: 3, borderRadius: 2, background: T.border, marginTop: 8, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 2,
            width: `${Math.min(100, Math.max(0, pct))}%`,
            background: fillColor }} />
        </div>
      )}
    </div>
  );
}

/* ══ STATUS PILL ══ */
function StatPill({ label, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '4px 10px 4px 8px', borderRadius: 20,
      border: `1px solid ${T.border}`, background: T.bg3,
      fontSize: 12, fontWeight: 500, color: T.fg, whiteSpace: 'nowrap',
    }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%',
        flexShrink: 0, background: color }} />
      {label}
    </div>
  );
}

/* ══ SECTION CARD ══ */
function SectionCard({ title, hint, children }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`,
      borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${T.border}`,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
        color: T.fg2, textTransform: 'uppercase',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>{title}</span>
        {hint && <span style={{ fontSize: 11, fontWeight: 400,
          textTransform: 'none', letterSpacing: 0, color: T.fg2,
          fontFamily: T.mono }}>{hint}</span>}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

/* ══ SETPOINTS PANEL ══ */
function SetpointsPanel({ tags, mqttClient, unitId }) {
  const r  = tags.rValues;
  const iv = tags.iValues;
  const sp = tags.rSetpoints;

  const pu  = PRESS_UNITS[iv[8]] || 'psi';
  const du  = PRESS_UNITS[iv[9]] || 'psi';
  const dtu = TEMP_UNITS[iv[11]] || '°C';

  const rows = [
    { label: 'Suction Pressure',      val: sp[1] ?? r[1], unit: pu,  spIdx: 1 },
    { label: 'Discharge Pressure',    val: sp[2] ?? r[2], unit: du,  spIdx: 2 },
    { label: 'Discharge Temperature', val: sp[3] ?? r[3], unit: dtu, spIdx: 3 },
  ];

  const publish = (idx, value) => {
    if (!mqttClient) return;
    mqttClient.publish(`units/${unitId}/from/real/${idx}`,
      String(value), { qos: 1, retain: true });
  };

  const [editing, setEditing] = useState(null);
  const [input,   setInput]   = useState('');

  return (
    <SectionCard title="Control Setpoints" hint="rSetpoints[1–3]">
      {rows.map(row => (
        <div key={row.spIdx} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 0', borderBottom: `1px solid ${T.border}`,
        }}>
          <div style={{ flex: 1, fontSize: 13, color: T.fg }}>{row.label}</div>
          {editing === row.spIdx ? (
            <>
              <input
                autoFocus
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const v = parseFloat(input);
                    if (!isNaN(v)) publish(row.spIdx, v);
                    setEditing(null);
                  }
                  if (e.key === 'Escape') setEditing(null);
                }}
                style={{
                  width: 80, padding: '4px 8px', borderRadius: 4,
                  border: `1px solid ${T.border}`, background: T.bg3,
                  color: T.fg, fontFamily: T.mono, fontSize: 13, textAlign: 'right',
                }}
              />
              <span style={{ fontSize: 11, color: T.fg2, width: 36 }}>{row.unit}</span>
              <button onClick={() => setEditing(null)}
                style={{ padding: '4px 10px', borderRadius: 4,
                  border: `1px solid ${T.border}`, background: 'none',
                  color: T.fg2, cursor: 'pointer', fontSize: 12 }}>✕</button>
            </>
          ) : (
            <>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: T.fg,
                width: 70, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {Number(row.val ?? 0).toFixed(1)}
              </span>
              <span style={{ fontSize: 11, color: T.fg2, width: 36 }}>{row.unit}</span>
              <button
                onClick={() => { setEditing(row.spIdx); setInput(String(Number(row.val ?? 0).toFixed(1))); }}
                style={{ padding: '4px 12px', borderRadius: 4,
                  border: `1px solid ${T.border}`, background: 'none',
                  color: T.fg2, cursor: 'pointer', fontSize: 12 }}>Edit</button>
            </>
          )}
        </div>
      ))}
      <p style={{ fontSize: 11, color: T.fg2, marginTop: 8, marginBottom: 0 }}>
        Press Enter to send · PLC echoes via rValues[1–3]
      </p>
    </SectionCard>
  );
}

/* ══ LIVE HMI PAGE ══ */
function LiveHMI({ tags, mqttClient, unitId }) {
  const r  = tags.rValues;
  const iv = tags.iValues;
  const xs = tags.xStatus;

  const pu  = PRESS_UNITS[iv[8]] || 'psi';
  const tu  = TEMP_UNITS[iv[10]] || '°C';
  const du  = PRESS_UNITS[iv[9]] || 'psi';
  const dtu = TEMP_UNITS[iv[11]] || '°C';

  const fmt = (v, d=1) => (v === null || v === undefined) ? null : Number(v).toFixed(d);

  /* KPI accent by value thresholds — extend as needed */
  const hydAcc = r[13] > 90 ? 'warn' : 'good';

  /* Piston state labels */
  const pistonLabels = ['Off','Extend','Retract','ROV','Slowing Down','Ramp Up','Alarm'];
  const pistonColors = [T.fg2, T.good, T.accent, '#2563eb', T.warn, T.good, T.crit];
  const pistonSt     = iv[4] || 0;

  /* Engine state */
  const engineLabels = ['Off','Running','Alarm'];
  const engineColors = [T.fg2, T.good, T.crit];
  const engineSt     = iv[1] || 0;

  /* Valve state */
  const valveLabels = ['Off','Running','Alarm','Neutral'];
  const valveColors = [T.fg2, T.good, T.crit, T.fg2];
  const valveSt     = iv[3] || 0;

  /* Tank state */
  const tankLabels = ['Normal','Low Level','Low Low Level'];
  const tankColors = [T.good, T.warn, T.crit];
  const tankSt     = iv[7] || 0;

  /* Cooler fan state */
  const coolerLabels = ['Off','Running','Alarm'];
  const coolerColors = [T.fg2, T.good, T.crit];
  const coolerSt     = iv[5] || 0;

  /* Cooler mode */
  const coolerMode   = iv[6] === 1 ? 'Heating' : 'Cooling';
  const coolerModeC  = iv[6] === 1 ? T.warn : T.accent;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: T.fg }}>Unit Overview</div>
        <div style={{ fontSize: 13, color: T.fg2 }}>Real-time process values · Last update: {tags._lastSeen ? tags._lastSeen.toLocaleTimeString('en-CA', { hour12: false }) : '—'}</div>
      </div>

      {/* ── KPI Row ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 10, marginBottom: 20,
      }}>
        <KpiTile label="Suction Pressure"    value={fmt(r[9],1)}  unit={pu}  accent="good" />
        <KpiTile label="Suction Temp"        value={fmt(r[11],1)} unit={tu}  accent="good" />
        <KpiTile label="Discharge Pressure"  value={fmt(r[10],1)} unit={du}  accent="good" />
        <KpiTile label="Discharge Temp"      value={fmt(r[12],1)} unit={dtu} accent="good" />
        <KpiTile label="Hydraulic Pressure"  value={fmt(r[13],0)} unit="psi" accent={hydAcc} />
      </div>

      {/* ── Sensor Grid ── */}
      <SectionCard title="Process Variables" hint="rValues / iValues">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 10,
        }}>
          <SensorItem label="Linear Transducer (Position)" value={fmt(r[14],1)} unit="mm"
            pct={r[14] != null ? (r[14] / 600) * 100 : 0} />
          <SensorItem label="SPM" value={fmt(r[4],1)} unit=""
            pct={r[4] != null ? (r[4] / 20) * 100 : 0} />
          <SensorItem label="Instantaneous Speed" value={fmt(r[8],2)} unit=""
            pct={r[8] != null ? (r[8] / 10) * 100 : 0} />
          <SensorItem label="Valve Speed" value={fmt(r[7],1)} unit="%"
            pct={r[7] ?? 0} />
          <SensorItem label="Engine State"
            value={engineLabels[engineSt] || 'Off'}
            accentColor={engineColors[engineSt]} />
          <SensorItem label="Piston State"
            value={pistonLabels[pistonSt] || 'Off'}
            accentColor={pistonColors[pistonSt]} />
          <SensorItem label="Cooler Fan"
            value={coolerLabels[coolerSt] || 'Off'}
            accentColor={coolerColors[coolerSt]} />
          <SensorItem label="Cooler Mode"
            value={coolerMode}
            accentColor={coolerModeC} />
        </div>
      </SectionCard>

      {/* ── Status Pills ── */}
      <SectionCard title="Status" hint="xStatus[0–n]">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <StatPill label={`Engine: ${engineLabels[engineSt] || 'Off'}`}   color={engineColors[engineSt]} />
          <StatPill label={`Valve: ${valveLabels[valveSt] || 'Off'}`}      color={valveColors[valveSt]} />
          <StatPill label={`Piston: ${pistonLabels[pistonSt] || 'Off'}`}   color={pistonColors[pistonSt]} />
          <StatPill label={`Tank: ${tankLabels[tankSt] || 'Normal'}`}      color={tankColors[tankSt]} />
          <StatPill label={`Cooler: ${coolerLabels[coolerSt] || 'Off'}`}   color={coolerColors[coolerSt]} />
          <StatPill label={`Cooler Mode: ${coolerMode}`}                   color={coolerModeC} />
          <StatPill label={xs[0] ? 'Low Oil Level' : 'Oil Level OK'}       color={xs[0] ? T.warn : T.good} />
          <StatPill label={xs[1] ? 'Low Low Oil Level ⚠' : 'Oil Level LL OK'} color={xs[1] ? T.crit : T.good} />
        </div>
      </SectionCard>

      {/* ── Setpoints ── */}
      <SetpointsPanel tags={tags} mqttClient={mqttClient} unitId={unitId} />
    </div>
  );
}

/* ══ STUB TAB ══ */
function StubTab({ name }) {
  return (
    <div style={{ padding: 60, textAlign: 'center', color: T.fg2 }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: T.fg }}>{name}</div>
      <div style={{ fontSize: 12, marginTop: 6 }}>Coming soon</div>
    </div>
  );
}

/* ══ MAIN COMPONENT ══ */
export default function UnitDetail({ auth, onLogout }) {
  const { unitId } = useParams();
  const navigate   = useNavigate();

  const [activeScreen, setActiveScreen] = useState('live');
  const [connStatus,   setConnStatus]   = useState('Connecting');
  const [lastSeen,     setLastSeen]     = useState(null);

  const tagsRef = useRef({
    rValues:    new Array(31).fill(null),
    iValues:    new Array(31).fill(0),
    xStatus:    new Array(16).fill(false),
    rSetpoints: new Array(10).fill(null),
    _lastSeen:  null,
  });
  const [tags, setTags] = useState({ ...tagsRef.current });
  const clientRef = useRef(null);

  const flushTags = useCallback(() => {
    setTags({
      rValues:    [...tagsRef.current.rValues],
      iValues:    [...tagsRef.current.iValues],
      xStatus:    [...tagsRef.current.xStatus],
      rSetpoints: [...tagsRef.current.rSetpoints],
      _lastSeen:  tagsRef.current._lastSeen,
    });
  }, []);

  useEffect(() => {
    const client = mqtt.connect(MQTT_URL, {
      username:  MQTT_USER,
      password:  MQTT_PASS,
      clientId:  `portal-${unitId}-${Math.random().toString(16).slice(2,8)}`,
      keepalive: 30,
      reconnectPeriod: 3000,
    });
    clientRef.current = client;

    client.on('connect', () => {
      setConnStatus('Connected');
      client.subscribe([
        `units/${unitId}/to/real/#`,
        `units/${unitId}/to/int/#`,
        `units/${unitId}/to/bool/#`,
        `units/${unitId}/to/alarm/#`,
      ], { qos: 0 });
    });
    client.on('reconnect',  () => setConnStatus('Connecting'));
    client.on('disconnect', () => setConnStatus('Disconnected'));
    client.on('error',      () => setConnStatus('Disconnected'));

    client.on('message', (topic, message) => {
      const val   = message.toString();
      const parts = topic.split('/');
      const type  = parts[3];
      const index = parseInt(parts[4], 10);
      if (isNaN(index)) return;

      const t = tagsRef.current;
      if      (type === 'real') t.rValues[index] = parseFloat(val);
      else if (type === 'int')  t.iValues[index] = parseInt(val, 10);
      else if (type === 'bool') t.xStatus[index] = val === '1' || val === 'true';
      t._lastSeen = new Date();

      setLastSeen(new Date());
      flushTags();
    });

    return () => { client.end(); clientRef.current = null; };
  }, [unitId, flushTags]);

  const dotColor = connStatus === 'Connected'  ? T.good
                 : connStatus === 'Connecting' ? T.warn : T.crit;

  /* Active alarm count stub */
  const alarmCount = 0;

  function renderScreen() {
    switch (activeScreen) {
      case 'live':    return <LiveHMI tags={tags} mqttClient={clientRef.current} unitId={unitId} />;
      case 'control': return <StubTab name="Control Setup" />;
      case 'alarm':   return <StubTab name="Alarms" />;
      case 'trend':   return <StubTab name="Trend" />;
      case 'rundata': return <StubTab name="Run Data" />;
      case 'admin':   return <StubTab name="Admin Setup" />;
      default:        return <StubTab name={activeScreen} />;
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg,
      fontFamily: T.ui, display: 'flex', flexDirection: 'column',
      color: T.fg }}>

      {/* ── Top bar ── */}
      <header style={{
        height: 52, background: T.bg2, borderBottom: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: 16,
        flexShrink: 0,
      }}>
        <button onClick={() => navigate('/')}
          style={{ padding: '5px 12px', borderRadius: 6,
            border: `1px solid ${T.border}`, background: 'none',
            color: T.fg2, cursor: 'pointer', fontSize: 12 }}>← Back</button>
        <span style={{ fontWeight: 600, fontSize: 15, color: T.fg, letterSpacing: '-0.02em' }}>
          Fluid<span style={{ color: T.accent }}>stream</span>
        </span>
        <div style={{ flex: 1 }} />
        {/* Connection status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor }} />
          <span style={{ fontSize: 12, color: T.fg2 }}>
            {connStatus}
            {lastSeen && connStatus === 'Connected' &&
              ` · ${lastSeen.toLocaleTimeString('en-CA', { hour12: false })}`}
          </span>
        </div>
        <button onClick={onLogout}
          style={{ padding: '5px 14px', borderRadius: 6,
            border: `1px solid ${T.border}`, background: 'none',
            color: T.fg2, cursor: 'pointer', fontSize: 12 }}>Sign Out</button>
      </header>

      {/* ── App shell ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Unit sidenav ── */}
        <nav style={{
          width: 168, flexShrink: 0, background: T.bg2,
          borderRight: `1px solid ${T.border}`,
          padding: '16px 0', overflowY: 'auto',
        }}>
          {/* Unit header */}
          <div style={{
            padding: '0 16px 14px', fontSize: 12, fontWeight: 600, color: T.fg,
            borderBottom: `1px solid ${T.border}`, marginBottom: 8,
          }}>
            {unitId}
            <div style={{ fontSize: 11, fontWeight: 400, color: T.fg2, marginTop: 2 }}>
              Unit Detail
            </div>
          </div>

          {/* Nav items */}
          {NAV.map(item => {
            const isActive = activeScreen === item.id;
            const badge = item.badge && alarmCount > 0;
            return (
              <div key={item.id}
                onClick={() => setActiveScreen(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 16px', cursor: 'pointer',
                  color: isActive ? T.accent : T.fg2,
                  fontSize: 13, fontWeight: 500,
                  borderLeft: `2px solid ${isActive ? T.accent : 'transparent'}`,
                  background: isActive ? T.bg3 : 'transparent',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = T.fg; e.currentTarget.style.background = T.bg3; }}}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = T.fg2; e.currentTarget.style.background = 'transparent'; }}}
              >
                {item.icon}
                <span style={{ flex: 1 }}>{item.label}</span>
                {badge && (
                  <span style={{
                    background: T.crit, color: '#fff',
                    fontSize: 10, fontWeight: 700, padding: '1px 5px',
                    borderRadius: 10, fontFamily: T.mono,
                  }}>{alarmCount}</span>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Page area ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {renderScreen()}
        </div>
      </div>
    </div>
  );
}
